import FormControl from '@material-ui/core/FormControl';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import ChangeAllPlacementCard from 'Components/Editor/Details/ChangeAllPlacements/ChangeAllPlacementCard';
import { UseVideoPlayerInstance } from 'Hooks/Editor/useVideoPlayerInstance';
import { DetectionPositionSpecification } from 'Models/Project/ProjectFilterSpecifications';
import EditorVideoDetection from 'Models/Videos/Detections/EditorVideoDetection';
import { VideoDetectionPoint } from 'Models/Videos/Detections/VideoDetectionCoordinate';
import { ChangeEvent, ReactElement } from 'react';

interface ChangeAllPlacementCardPositionProps {
	position: DetectionPositionSpecification | null;
	onPositionChange: (position: DetectionPositionSpecification | null) => void;
	instance: UseVideoPlayerInstance;
	selectedDetections: EditorVideoDetection[];
}

const INITIAL_POSITION_VALUE: DetectionPositionSpecification = {
	point: { x: 0, y: 0 },
	offset: { x: 0, y: 0 },
};

const ChangeAllPlacementCardPosition = (props: ChangeAllPlacementCardPositionProps): ReactElement => {
	const isChecked = props.position !== null;
	const x = props.position?.point.x ?? 0;
	const y = props.position?.point.y ?? 0;
	const offsetX = props.position?.offset?.x ?? 0; // Offset can be undefined due to legacy
	const offsetY = props.position?.offset?.y ?? 0; // Offset can be undefined due to legacy

	const videoSize = {
		width: props.instance.video?.width ?? Number.MAX_SAFE_INTEGER,
		height: props.instance.video?.height ?? Number.MAX_SAFE_INTEGER,
	};

	const getLeftPoint = (detection: EditorVideoDetection): VideoDetectionPoint => {
		let leftPoint = detection.initialCoordinates.point1;
		if (leftPoint.x > detection.initialCoordinates.point2.x) leftPoint = detection.initialCoordinates.point2;
		if (leftPoint.x > detection.initialCoordinates.point3.x) leftPoint = detection.initialCoordinates.point3;
		if (leftPoint.x > detection.initialCoordinates.point4.x) leftPoint = detection.initialCoordinates.point4;
		return leftPoint;
	};

	const getTopPoint = (detection: EditorVideoDetection): VideoDetectionPoint => {
		let bottomPoint = detection.initialCoordinates.point1;
		if (bottomPoint.y > detection.initialCoordinates.point2.y) bottomPoint = detection.initialCoordinates.point2;
		if (bottomPoint.y > detection.initialCoordinates.point3.y) bottomPoint = detection.initialCoordinates.point3;
		if (bottomPoint.y > detection.initialCoordinates.point4.y) bottomPoint = detection.initialCoordinates.point4;
		return bottomPoint;
	};

	const getRightPoint = (detection: EditorVideoDetection): VideoDetectionPoint => {
		let rightPoint = detection.initialCoordinates.point1;
		if (rightPoint.x < detection.initialCoordinates.point2.x) rightPoint = detection.initialCoordinates.point2;
		if (rightPoint.x < detection.initialCoordinates.point3.x) rightPoint = detection.initialCoordinates.point3;
		if (rightPoint.x < detection.initialCoordinates.point4.x) rightPoint = detection.initialCoordinates.point4;
		return rightPoint;
	};

	const getBottomPoint = (detection: EditorVideoDetection): VideoDetectionPoint => {
		let topPoint = detection.initialCoordinates.point1;
		if (topPoint.y < detection.initialCoordinates.point2.y) topPoint = detection.initialCoordinates.point2;
		if (topPoint.y < detection.initialCoordinates.point3.y) topPoint = detection.initialCoordinates.point3;
		if (topPoint.y < detection.initialCoordinates.point4.y) topPoint = detection.initialCoordinates.point4;
		return topPoint;
	};

	const handlePlacementCardCheckedChanged = (checked: boolean): void => {
		let defaultValue = INITIAL_POSITION_VALUE;
		if (props.selectedDetections.length > 0) {
			// This considers the fact that coordinate system is top to bottom, but in fact this does not affect anything but naming.
			const x = props.selectedDetections.reduce((acc, x) => Math.min(acc, getLeftPoint(x).x), Number.MAX_SAFE_INTEGER);
			const y = props.selectedDetections.reduce((acc, x) => Math.min(acc, getTopPoint(x).y), Number.MAX_SAFE_INTEGER);
			const offsetX = props.selectedDetections.reduce((acc, x) => Math.max(acc, getRightPoint(x).x), 0) - x;
			const offsetY = props.selectedDetections.reduce((acc, x) => Math.max(acc, getBottomPoint(x).y), 0) - y;
			defaultValue = {
				point: { x: Math.floor(x), y: Math.floor(y) },
				offset: { x: Math.ceil(offsetX), y: Math.ceil(offsetY) },
			};
		}
		if (checked) props.onPositionChange(defaultValue);
		else props.onPositionChange(null);
	};

	const handleXChanged = ({ target }: ChangeEvent<HTMLInputElement>): void => {
		const value = Number(target.value);
		if (!isNaN(value)) props.onPositionChange({ point: { x: value, y }, offset: { x: offsetX, y: offsetY } });
	};

	const handleYChanged = ({ target }: ChangeEvent<HTMLInputElement>): void => {
		const value = Number(target.value);
		if (!isNaN(value)) props.onPositionChange({ point: { y: value, x }, offset: { x: offsetX, y: offsetY } });
	};

	const handleOffsetXChanged = ({ target }: ChangeEvent<HTMLInputElement>): void => {
		const value = Number(target.value);
		if (!isNaN(value)) props.onPositionChange({ point: { x, y }, offset: { x: value, y: offsetY } });
	};

	const handleOffsetYChanged = ({ target }: ChangeEvent<HTMLInputElement>): void => {
		const value = Number(target.value);
		if (!isNaN(value)) props.onPositionChange({ point: { x, y }, offset: { x: offsetX, y: value } });
	};

	return (
		<ChangeAllPlacementCard
			name="Select position"
			onCheckedChanged={handlePlacementCardCheckedChanged}
			isChecked={isChecked}
		>
			<FormControl fullWidth={true} margin="normal">
				<InputLabel required={true}>X</InputLabel>
				<Input
					onChange={handleXChanged}
					value={x}
					required={true}
					inputProps={{
						step: 1,
						min: 0,
						max: videoSize.width,
						type: 'number',
					}}
				/>
			</FormControl>
			<FormControl fullWidth={true} margin="normal">
				<InputLabel required={true}>Y</InputLabel>
				<Input
					onChange={handleYChanged}
					value={y}
					required={true}
					inputProps={{
						step: 1,
						min: 0,
						max: videoSize.height,
						type: 'number',
					}}
				/>
			</FormControl>
			<FormControl fullWidth={true} margin="normal">
				<InputLabel required={true}>X offset</InputLabel>
				<Input
					onChange={handleOffsetXChanged}
					value={offsetX}
					required={true}
					inputProps={{
						step: 1,
						min: 0,
						max: videoSize.width - x,
						type: 'number',
					}}
				/>
			</FormControl>
			<FormControl fullWidth={true} margin="normal">
				<InputLabel required={true}>Y offset</InputLabel>
				<Input
					onChange={handleOffsetYChanged}
					value={offsetY}
					required={true}
					inputProps={{
						step: 1,
						min: 0,
						max: videoSize.height - y,
						type: 'number',
					}}
				/>
			</FormControl>
		</ChangeAllPlacementCard>
	);
};

export default ChangeAllPlacementCardPosition;
